/**
 * @fileoverview Tonight's per-category event counts for the city-header
 * Categories disclosure and the ≤720 sitemap chip group (WEB-1180 / DES-108).
 *
 * Source: the canonical count endpoint (PIP-96) — `GET /api/v1/events/count`
 * `byCategory` with `date=today`, the same keyword the city-home tonight docket
 * uses (`today` is the API's tonight window, API-1746). One source, no second
 * count path.
 *
 * Lazy by design: the header is mounted on every city page, so the request is
 * only made once a visitor shows intent (`enabled` flips true on hover, focus
 * or open). The result is memoised per city for the page session so reopening
 * the panel never refetches. Fail-closed: any non-OK response, malformed
 * payload or thrown error yields `null` (and is not memoised, so the next open
 * retries); callers then render the ten without counts — never a fabricated
 * zero and never a hidden category.
 */

'use client';

import * as React from 'react';
import { z } from 'zod';
import type { CityCode } from '@loci/city-editions';

import { API_ENDPOINTS } from '@/config/api';
import { CATEGORY_ORDER, type CategorySlug } from '@/config/categories';

/** Tonight count per canonical slug. A slug absent from a good payload is a real 0. */
export type CategoryTonightCounts = Readonly<Record<CategorySlug, number>>;

const countResponseSchema = z.object({
  byCategory: z
    .array(z.object({ slug: z.string(), count: z.number().int().nonnegative() }))
    .default([]),
});

const cache = new Map<CityCode, Promise<CategoryTonightCounts | null>>();

/** Test seam: forget every memoised city. */
export function resetCategoryTonightCountsCache(): void {
  cache.clear();
}

export function buildCategoryTonightCountsUrl(cityCode: CityCode): string {
  const params = new URLSearchParams({ city: cityCode, date: 'today' });
  return `${API_ENDPOINTS.rest}/api/v1/events/count?${params.toString()}`;
}

/** Folds a `byCategory` payload into the closed ten; unknown slugs are dropped. */
export function toCategoryTonightCounts(raw: unknown): CategoryTonightCounts | null {
  const parsed = countResponseSchema.safeParse(raw);
  if (!parsed.success) return null;
  const bySlug = new Map(parsed.data.byCategory.map((row) => [row.slug, row.count]));
  const counts = {} as Record<CategorySlug, number>;
  for (const slug of CATEGORY_ORDER) {
    counts[slug] = bySlug.get(slug) ?? 0;
  }
  return counts;
}

async function fetchCounts(cityCode: CityCode): Promise<CategoryTonightCounts | null> {
  try {
    const res = await fetch(buildCategoryTonightCountsUrl(cityCode), { credentials: 'omit' });
    if (!res.ok) return null;
    return toCategoryTonightCounts(await res.json());
  } catch {
    return null;
  }
}

function loadCounts(cityCode: CityCode): Promise<CategoryTonightCounts | null> {
  const cached = cache.get(cityCode);
  if (cached) return cached;
  const pending = fetchCounts(cityCode).then((counts) => {
    // Only a good payload is memoised — a failure retries on the next intent.
    if (counts === null) cache.delete(cityCode);
    return counts;
  });
  cache.set(cityCode, pending);
  return pending;
}

/**
 * Tonight counts for `cityCode`, or `null` until `enabled` has been true and
 * the request has settled successfully.
 */
export function useCategoryTonightCounts(
  cityCode: CityCode,
  enabled: boolean,
): CategoryTonightCounts | null {
  const [counts, setCounts] = React.useState<CategoryTonightCounts | null>(null);

  React.useEffect(() => {
    if (!enabled) return;
    let active = true;
    void loadCounts(cityCode).then((next) => {
      if (active && next !== null) setCounts(next);
    });
    return () => {
      active = false;
    };
  }, [cityCode, enabled]);

  return counts;
}
